import { useState } from 'react';

import { createUser, updateUser } from '../api.js';
import Modal from '../ui/Modal.jsx';
import { notify } from '../ui/notify.jsx';
import { buildPayload, validateUserForm, wouldRemoveLastAdmin } from './users.js';

export default function UserModal({ mode, user, roles, users, domain, onClose, onSaved }) {
  const isEdit = mode === 'edit';

  const [firstName, setFirstName] = useState(isEdit ? user.first_name : '');
  const [lastName, setLastName] = useState(isEdit ? user.last_name : '');
  const [email, setEmail] = useState(isEdit ? user.email : '');
  const [selectedRoles, setSelectedRoles] = useState(isEdit ? [...user.roles] : []);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);

  const toggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const validation = validateUserForm({
      firstName, lastName, email, roles: selectedRoles, domain,
    });
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    if (isEdit && wouldRemoveLastAdmin(users, user.email, selectedRoles, true)) {
      setServerError('Cannot remove Admin from the only remaining admin.');
      return;
    }

    setErrors({});
    setServerError(null);
    setBusy(true);

    try {
      const payload = buildPayload({
        firstName,
        lastName,
        email,
        roles: selectedRoles,
        active: isEdit ? user.active : undefined,
      });

      if (isEdit) {
        await updateUser(user.email, payload);
        notify(`${firstName.trim()} ${lastName.trim()} updated.`);
      } else {
        await createUser(payload);
        notify(`${firstName.trim()} ${lastName.trim()} added.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={onSubmit}>
        <header className="modal-header">
          <h3>{isEdit ? 'Edit user' : 'Add user'}</h3>
          <button type="button" className="icon-button quiet modal-close" onClick={onClose}
                  aria-label="Close">
            ×
          </button>
        </header>

        <label>
          First name
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
          />
          {errors.firstName && <span className="field-error">{errors.firstName}</span>}
        </label>

        <label>
          Last name
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {errors.lastName && <span className="field-error">{errors.lastName}</span>}
        </label>

        <label>
          Email
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <span className="field-error">{errors.email}</span>}
        </label>

        <fieldset className="choice-set">
          <legend>Roles</legend>
          {roles.map((role) => (
            <label key={role} className="choice">
              <input
                type="checkbox"
                checked={selectedRoles.includes(role)}
                onChange={() => toggleRole(role)}
              />
              {role}
            </label>
          ))}
          {errors.roles && <span className="field-error">{errors.roles}</span>}
        </fieldset>

        {serverError && <p className="error">{serverError}</p>}

        <menu>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </button>
        </menu>
      </form>
    </Modal>
  );
}
